import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DATESTR_FMT } from 'tdr-common';
import { Search } from 'lucide-react';
import Profile2User from 'iconsax-react/dist/esm/Profile2User';
import Calendar from 'iconsax-react/dist/esm/Calendar';
import Clock from 'iconsax-react/dist/esm/Clock';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AvailabilityGuardContext } from '../../../routes/AvailabilityGuard';
import { usePendingParameters } from '../../../context/open-parameters/PendingParametersContext';
import {
	type PendingParameters,
	ParameterField,
	validatePendingParameters
} from '../../../helpers/open-parameters/validatePendingParameters';
import { getNextUnsetParameterField } from '../../../helpers/open-parameters/getNextUnsetParameterField';
import { getLeadGenPartySize } from '../../../helpers/open-parameters/getLeadGenPartySize';
import {
	analytics,
	FILTER_VIEWED,
	OPEN_PARAMETERS_SEARCH_BLOCKED,
	SHOW_RESULTS
} from '../../../helpers/analytics';
import { useFilterAnalytics } from '../../../helpers/analytics/filterContext';
import { useInViewOnce } from '../../../hooks/useInViewOnce';
import { DesktopPartySizeSelector } from './DesktopPartySizeSelector';
import { DesktopOpenCalendar } from './DesktopOpenCalendar';
import { DesktopOpenTimeSelector } from './DesktopOpenTimeSelector';
import { OpenParametersSegmentTrigger } from './OpenParametersSegmentTrigger';
import { DesktopParameterPopoverShell } from './DesktopParameterPopoverShell';
import {
	desktopPickerCalendarAlignOffset,
	desktopPickerPopoverContentClassName,
	desktopPickerPopoverSideOffset
} from './desktopParameterPickerStyles';

type Field = ParameterField;
const AUTO_ADVANCE_DELAY_MS = 350;

export const DesktopOpenSearchHeader = () => {
	const { t, i18n } = useTranslation();
	const { restaurant } = useOutletContext<AvailabilityGuardContext>();
	const { pending, setPending, commit, triggerRefresh } = usePendingParameters();

	const [openField, setOpenField] = useState<Field | null>(null);
	const [showErrors, setShowErrors] = useState(false);
	const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const filterCtx = useFilterAnalytics('global explore');
	useInViewOnce(rootRef, () => analytics.track(FILTER_VIEWED, { ...filterCtx }));

	useEffect(
		() => () => {
			if (advanceTimer.current) {
				clearTimeout(advanceTimer.current);
			}
		},
		[]
	);

	const scheduleAdvance = (next: Field | null) => {
		if (advanceTimer.current) {
			clearTimeout(advanceTimer.current);
		}
		advanceTimer.current = setTimeout(() => setOpenField(next), AUTO_ADVANCE_DELAY_MS);
	};

	const advanceAfter = (field: Field, nextPending: PendingParameters) => {
		scheduleAdvance(getNextUnsetParameterField(nextPending, field));
	};

	const validation = useMemo(() => validatePendingParameters(pending), [pending]);

	const hasParty =
		typeof pending.groupSize === 'number' && pending.groupSize > 0;
	const { leadGenValue } = getLeadGenPartySize(restaurant);
	const guestLabel = hasParty
		? (leadGenValue !== null && pending.groupSize === leadGenValue
			? `${pending.groupSize}+`
			: `${pending.groupSize} ${pending.groupSize === 1 ? t('labels.guest', { defaultValue: 'Guest' }) : t('labels.guests', { defaultValue: 'Guests' })}`)
		: t('titles.party_size');

	const hasDate = Boolean(pending.date);
	const dateLabel = hasDate
		? DateTime.fromFormat(pending.date!, DATESTR_FMT)
				.setLocale(i18n.language === 'fr' ? 'fr-CA' : 'en')
				.toLocaleString({
					month: 'short',
					day: 'numeric',
					year: 'numeric'
				})
		: t('labels.date', { defaultValue: 'Date' });

	const hasTime = Boolean(pending.time);
	const timeLabel = hasTime
		? DateTime.fromFormat(pending.time!, 'HH:mm').toFormat('h:mm a').toUpperCase()
		: t('labels.time', { defaultValue: 'Time' });

	const handleSearch = () => {
		if (validation.status === 'partial') {
			setShowErrors(true);
			analytics.track(OPEN_PARAMETERS_SEARCH_BLOCKED, {
				source: 'open_parameters_desktop',
				missingFields: Object.keys(validation.errors)
			});
			return;
		}
		// "Viewed results": the user filled all params and clicked Search.
		if (validation.status === 'complete') {
			analytics.track(SHOW_RESULTS, {
				source: 'open_parameters_desktop',
				groupSize: pending.groupSize ?? null,
				date: pending.date ?? null,
				time: pending.time ?? null,
				leadGenSelected: leadGenValue !== null && pending.groupSize === leadGenValue
			});
		}
		commit();
		triggerRefresh();
	};

	const makeOpenChangeHandler = (field: Field) => (open: boolean) => {
		if (advanceTimer.current) {
			clearTimeout(advanceTimer.current);
			advanceTimer.current = null;
		}
		if (open) {
			setOpenField(field);
		}
 else {
			setOpenField(prev => (prev === field ? null : prev));
		}
	};

	const preventAutoFocus = (event: Event) => {
		event.preventDefault();
	};

	return (
		<div ref={rootRef} className="relative box-border flex w-[758px] max-w-full shrink-0 items-stretch gap-4 rounded-[40px] border border-white/10 bg-[#2f3238] p-2">
			<div className="flex min-w-0 max-w-[590px] shrink grow-0 basis-[590px] items-stretch">
				<div className="flex min-w-0 flex-[1_0_0] items-center">
					<Popover open={openField === 'groupSize'} onOpenChange={makeOpenChangeHandler('groupSize')}>
						<PopoverTrigger asChild>
							<OpenParametersSegmentTrigger
								icon={<Profile2User size={18} color="#B58D45" />}
								label={guestLabel}
								hasValue={hasParty}
								isOpen={openField === 'groupSize'}
								hasError={showErrors && !!validation.errors.groupSize}
								onClear={() => setPending({ groupSize: undefined })}
							/>
						</PopoverTrigger>
						<PopoverContent
							align="start"
							sideOffset={desktopPickerPopoverSideOffset}
							className={desktopPickerPopoverContentClassName}
							onOpenAutoFocus={preventAutoFocus}
							onCloseAutoFocus={preventAutoFocus}
						>
							<DesktopParameterPopoverShell width="party">
								<DesktopPartySizeSelector
									restaurant={restaurant}
									value={pending.groupSize}
									onChange={n => {
										const next: PendingParameters = { ...pending, groupSize: n };
										setPending({ groupSize: n });
										if (n > 0) {
											advanceAfter('groupSize', next);
										}
									}}
								/>
							</DesktopParameterPopoverShell>
						</PopoverContent>
					</Popover>
				</div>

				{openField === null ? (
					<div className="h-8 w-px shrink-0 self-center bg-white/10" aria-hidden />
				) : null}

				<div className="flex min-w-0 flex-[1_0_0] items-center">
					<Popover open={openField === 'date'} onOpenChange={makeOpenChangeHandler('date')}>
						<PopoverTrigger asChild>
							<OpenParametersSegmentTrigger
								icon={<Calendar size={18} color="#B58D45" />}
								label={dateLabel}
								hasValue={hasDate}
								isOpen={openField === 'date'}
								hasError={showErrors && !!validation.errors.date}
								onClear={() => setPending({ date: undefined })}
							/>
						</PopoverTrigger>
						<PopoverContent
							align="start"
							alignOffset={desktopPickerCalendarAlignOffset}
							avoidCollisions={false}
							sideOffset={desktopPickerPopoverSideOffset}
							className={desktopPickerPopoverContentClassName}
							onOpenAutoFocus={preventAutoFocus}
							onCloseAutoFocus={preventAutoFocus}
						>
							<DesktopParameterPopoverShell width="calendar">
								<DesktopOpenCalendar
									restaurant={restaurant}
									value={pending.date}
									onChange={d => {
										const next: PendingParameters = { ...pending, date: d };
										setPending({ date: d });
										advanceAfter('date', next);
									}}
								/>
							</DesktopParameterPopoverShell>
						</PopoverContent>
					</Popover>
				</div>

				{openField === null || openField === 'groupSize' ? (
					<div className="h-8 w-px shrink-0 self-center bg-white/10" aria-hidden />
				) : null}

				<div className="flex min-w-0 flex-[1_0_0] items-center">
					<Popover open={openField === 'time'} onOpenChange={makeOpenChangeHandler('time')}>
						<PopoverTrigger asChild>
							<OpenParametersSegmentTrigger
								icon={<Clock size={18} color="#B58D45" />}
								label={timeLabel}
								hasValue={hasTime}
								isOpen={openField === 'time'}
								hasError={showErrors && !!validation.errors.time}
								onClear={() => setPending({ time: undefined })}
							/>
						</PopoverTrigger>
						<PopoverContent
							align="start"
							sideOffset={desktopPickerPopoverSideOffset}
							className={desktopPickerPopoverContentClassName}
							onOpenAutoFocus={preventAutoFocus}
							onCloseAutoFocus={preventAutoFocus}
						>
							<DesktopParameterPopoverShell width="time">
								<DesktopOpenTimeSelector
									restaurant={restaurant}
									value={pending.time}
									onChange={time => {
										const next: PendingParameters = { ...pending, time };
										setPending({ time });
										advanceAfter('time', next);
									}}
								/>
							</DesktopParameterPopoverShell>
						</PopoverContent>
					</Popover>
				</div>
			</div>

			<button
				type="button"
				onClick={handleSearch}
				className="flex shrink-0 cursor-pointer items-center gap-3 self-center rounded-[32px] bg-[#a8813a] px-5 py-5 font-poppins text-lg font-medium leading-none text-white hover:opacity-95"
			>
				<Search className="size-[18px] shrink-0 text-white" strokeWidth={2} />
				{t('actions.search', { defaultValue: 'Search' })}
			</button>
		</div>
	);
};
